import React, { useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import BackgroundImageCst from "@components/BackgroundImageCst";
import HeroCst from "@components/HeroCst";
import GoodsReceiptsFormCst from "../GoodsReceiptsFormCst";
import {
  useFetchDataDetail,
  useFetchDetail,
  usePostDetail,
} from "@hooks/useFetchDetail";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "@components/Breadcrumb";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import { useAddEditGoodReceipt } from "../hooks/useAddEditGoodReceipt";
import dayjs from "dayjs";

const AddGoodReceipt: React.FC = () => {
  const { t } = useTranslation(["goods-receipts-page"]);
  const { id, idProject, idCostModel, idCostModelDetail } = useParams();
  const navigate = useNavigate();

  const { columns } = useFetchDetail(
    ServicesURL.getCostModelDetail,
    undefined,
    PagesID["projects.cost-goods-receipts"],
  );

  /*Get the project information*/
  const { dataDetail: dataProject } = useFetchDataDetail(
    `${ServicesURL.getProjects}/${idProject}?corporateId=${id}`,
    PagesID["projects"],
  );

  /*Get the cost model information*/
  const { dataDetail: dataCostModel } = useFetchDataDetail(
    `${ServicesURL.postCostModel.replace(":budgetProjectId", String(idProject))}/${idCostModel}?corporateId=${id}`,
    PagesID["projects.cost-model"],
  );

  /*Get the cost detail information*/
  const { dataDetail: dataCostDetail } = useFetchDataDetail(
    `${ServicesURL.getCostModelDetail}/${idCostModelDetail}?corporateId=${id}`,
    PagesID["projects.cost-detail"],
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetGoodReceiptAssociations,
    setCustomerAssociation,
    customerAssociation,
    openAssociations,
  } = useAddEditGoodReceipt([]);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { trigger: triggerGoodReceipt } = usePostDetail(
    `${ServicesURL.postGoodsReceipts.replaceAll(":costId", String(idCostModelDetail))}?corporateId=${id}`,
  );

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToSend: any = {
      value: inputForm.value || null,
      notes: inputForm.notes || null,
      status: inputForm.status || null,
      funded: inputForm.funded || false,
      customer_id:
        customerAssociation && customerAssociation.length > 0
          ? parseInt(customerAssociation[0].id)
          : null,
    };

    try {
      await triggerGoodReceipt(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
  };

  /*Building the data to send at the form with project and cost info*/
  const formField = useMemo(() => {
    if (
      dataCostModel.dto != null &&
      dataProject.dto != null &&
      dataCostDetail != null &&
      Object.keys(dataCostDetail).length > 0
    ) {
      return {
        info_detail_cost: dataCostDetail || null,
        info_project: dataProject?.dto || null,
        info_cost_model: dataCostModel?.dto || null,
      };
    } else {
      return null;
    }
  }, [dataCostModel, dataProject, dataCostDetail]);

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-contracts.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={t("add.hero-title")}
          description={t("add.hero-description")}
        />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <GoodsReceiptsFormCst
              row={formField}
              columns={columns}
              onSubmit={onSubmit}
              backButton={() => {
                navigate(-1);
              }}
              setOpenAssociation={(accessorKey, open) =>
                setOpenAssociation(accessorKey, open)
              }
              handleAssociation={(accessorKey, value) =>
                handleAssociation(accessorKey, value)
              }
              handleDelete={(accessorKey, value) =>
                handleDelete(accessorKey, value)
              }
              getValues={(accessorKey) => getValues(accessorKey)}
              resetGoodReceiptAssociations={resetGoodReceiptAssociations}
              association={openAssociations}
              fullWidth={true}
              customerAssociation={customerAssociation}
              setCustomerAssociation={setCustomerAssociation}
              isNew={true}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AddGoodReceipt;
